import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, Output } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ConfirmationService } from "primeng/api";
import { ChildEvent, ScrBaseComponent } from "src/app/components/base.component";
import { AddCreditCardValidators } from "src/app/directives/credit-card.directive";
import { MustMatch, ValidateChecksumReactive } from "src/app/directives/validators.directive";
import { AccountType } from "src/app/models/payment.model";
import { ConfigService } from "src/app/services/config.service";
import { PaymentMethodsService } from "src/app/services/paymentmethod.service";

@Component({
  selector: 'app-add-ach',
  templateUrl: './add-ach.component.html',
  styleUrls: ['./add-ach.component.scss']
})
export class AddACHComponent extends ScrBaseComponent {
  accountTypes!: AccountType[];
  achForm!: UntypedFormGroup;
  submitted = false;

  achIdentificationCode?: string;
  achMaxPaymentLimit = 500_000_000;
  public JSON = JSON;

  @Output() emitAction = new EventEmitter<ChildEvent>();

  constructor(private confirmService: ConfirmationService,
    private formBuilder: UntypedFormBuilder,
    private paymentService: PaymentMethodsService,
    private configService: ConfigService) {
    super();
    this.createFormControls();
    this.configService.getConfig().subscribe(cfg => {
      this.achIdentificationCode = cfg.achIdentificationCode;
      this.achMaxPaymentLimit = cfg.achMaxPaymentLimit;
    });
  }

  //Easy way to get controls
  get f() { return this.achForm.controls; }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prepareForSubmit(string: any, value: any) {

    if (typeof value === 'string')
      return value.trim();

    return value;
  }

  submit() {
    this.clearMessages();
    this.submitted = true;

    // the p-checkbox doesn't get marked as dirty if user skips by.
    // if we mark it dirty here, it doesn't matter.  it is either checked or not.
    this.achForm.controls['consent'].markAsDirty();

    if (!this.achForm.valid) {
      this.scrollToFirstError();
      return;
    }


    const prunedData = this.achForm.value;
    const formData = JSON.parse(JSON.stringify(prunedData, this.prepareForSubmit));
    this.observe(this.paymentService.addAchPaymentMethod(formData), {
      onSuccess: (res) => {
        if (res.messages && res.messages.length > 0) {
          this.emitAction.emit({ action: 'reload', value: res.ach?.achPaymentMethodId, message: res.messages[0].message });
        }
        else {
          this.emitAction.emit({ action: 'reload', value: res.ach?.achPaymentMethodId, messageCode: 'scr.cc.updated' });
        }
        return false;
      },
      onError: (error: HttpErrorResponse) => {
        this.displayErrorMessage(error.error);
      }
    });

  }

  createFormControls() {
    this.accountTypes = [
      { name: "Checking", type: "Checking" },
      { name: "Corporate Checking", type: "CorporateChecking" },
      { name: "Savings", type: "Saving" }
    ];
    this.achForm = this.formBuilder.group(
      {
        accountOwnerName: ['', [Validators.required, Validators.maxLength(80), AddCreditCardValidators.WhiteSpaceValidation()]],
        description: ['', [Validators.maxLength(80)]],
        accountType: ['CorporateChecking', [Validators.required, Validators.maxLength(32)]],
        routingNumber: ['', [Validators.required, Validators.minLength(9), Validators.maxLength(9), AddCreditCardValidators.WhiteSpaceValidation()]],
        accountNumber: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(17), AddCreditCardValidators.WhiteSpaceValidation()]],
        limit: [undefined, [Validators.required]],
        bankName: ['', [Validators.required, Validators.maxLength(40)]],
        confirmAccountNumber: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(17), AddCreditCardValidators.WhiteSpaceValidation()]],
        defaultPaymentMethod: [false],
        consent: [false, [Validators.required]],
      }, {
      validators: [MustMatch('accountNumber', 'confirmAccountNumber'), ValidateChecksumReactive('routingNumber')],
      updateOn: 'change'
    });
  }

  confirmCancelDialog() {
    this.confirmService.confirm({
      key: 'confirmCancel',
      header: 'Confirm Cancellation',
      message: 'Are you sure you want to cancel the request?',
      accept: () => {
        this.achForm.reset();
        this.submitted = false;
        this.emitAction.emit({ action: 'cancel', value: 'cancel' });
      },
      reject: () => {
        this.confirmService.close();
      }
    });
  }

  accountNumberConfirmation() {
    return this.achForm.controls.confirmAccountNumber.value !== this.achForm.controls.confirmAccountNumber.value;
  }

  accountNumberEmpty() {
    return this.achForm.controls.confirmAccountNumber.value !== undefined;
  }
}